import DoctorSignature, { SIGNATURE_FOOTER_SPACE } from "./DoctorSignature";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";

// Registrar fuente Roboto
Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "http://fonts.gstatic.com/s/roboto/v15/W5F8_SL0XFawnjxHGsZjJA.ttf",
    },
    {
      src: "http://fonts.gstatic.com/s/roboto/v15/bdHGHleUa-ndQCOrdpfxfw.ttf",
      fontStyle: "normal",
      fontWeight: 700,
    },
  ],
});

// Estilos
const styles = StyleSheet.create({
  page: { fontSize: 12, color: "#000", paddingBottom: SIGNATURE_FOOTER_SPACE },
  header: {
    paddingHorizontal: 40,
    paddingTop: 20,
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  logo: { width: "100%", height: "15%" },
  watermark: {
    position: "absolute",
    top: "35%",
    left: "15%",
    width: "70%",
    opacity: 0.1,
    zIndex: -10,
  },
  title: {
    textAlign: "center",
    fontSize: 16,
    fontFamily: "Roboto",
    fontWeight: 700,
    marginBottom: 20,
  },
  text: {
    marginBottom: 5,
    fontSize: 10,
    fontFamily: "Roboto",
    fontWeight: 400,
    color: "#000",
  },
  textBold: { fontWeight: 700 },
  section: { paddingHorizontal: 40, marginBottom: 10 },
  sectionTitle: {
    fontSize: 12,
    fontFamily: "Roboto",
    fontWeight: 700,
    marginBottom: 5,
    marginTop: 10,
  },
  table: {
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row",
  },
  tableColHeader: {
    width: "25%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#f0f0f0",
  },
  tableCol: {
    width: "25%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableColHeaderWide: {
    width: "30%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#f0f0f0",
  },
  tableColWide: {
    width: "30%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableColHeaderNarrow: {
    width: "20%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#f0f0f0",
  },
  tableColNarrow: {
    width: "20%",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCellHeader: {
    margin: 3,
    fontSize: 10,
    fontWeight: "bold",
  },
  tableCell: {
    margin: 3,
    fontSize: 10,
  },
  firmas: {
    marginTop: 40,
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
});

// Utilidades
function formatFecha(fecha) {
  if (!fecha) return "";
  const date = new Date(fecha);
  return `${String(date.getDate()).padStart(2, "0")}/${String(
    date.getMonth() + 1
  ).padStart(2, "0")}/${date.getFullYear()}`;
}

function agruparPorIdVacuna(registros) {
  const agrupado = {};
  registros.forEach((registro) => {
    const id = registro.id_vacuna || "desconocido";
    if (!agrupado[id]) agrupado[id] = [];
    agrupado[id].push({
      fecha: registro.fecha_aplicacion,
      observacion: registro.observacion,
      edad: registro.edad || "",
      vacuna: registro.vacuna || {},
      abreviatura: registro.abreviatura || "",
      label: registro.label || "",
    });
  });
  return agrupado;
}

// Tabla de vacuna
const TablaVacuna = ({ id, lista }) => (
  <View key={id} style={{ marginBottom: 15 }}>
    <Text style={styles.sectionTitle}>
      {lista?.[0]?.vacuna?.nombre || "Vacuna desconocida"}
    </Text>
    <View style={styles.table}>
      <View style={styles.tableRow}>
        <View style={styles.tableColHeader}>
          <Text style={styles.tableCellHeader}>Dosis</Text>
        </View>
        <View style={styles.tableColHeader}>
          <Text style={styles.tableCellHeader}>Fecha</Text>
        </View>
        <View style={styles.tableColHeaderWide}>
          <Text style={styles.tableCellHeader}>Observación</Text>
        </View>
        <View style={styles.tableColHeaderNarrow}>
          <Text style={styles.tableCellHeader}>Edad</Text>
        </View>
      </View>
      {lista.map((item, idx) => (
        <View style={styles.tableRow} key={idx}>
          <View style={styles.tableCol}>
            <Text style={styles.tableCell}>{item.abreviatura || "N/A"}</Text>
          </View>
          <View style={styles.tableCol}>
            <Text style={styles.tableCell}>{formatFecha(item.fecha)}</Text>
          </View>
          <View style={styles.tableColWide}>
            <Text style={styles.tableCell}>{item.observacion || "-"}</Text>
          </View>
          <View style={styles.tableColNarrow}>
            <Text style={styles.tableCell}>
              {item.label !== undefined && item.label !== null
                ? item.label < 12
                  ? `${item.label} meses`
                  : item.label % 12 === 0
                  ? `${Math.floor(item.label / 12)} años`
                  : `${Math.floor(item.label / 12)} años ${
                      item.label % 12
                    } meses`
                : "-"}
            </Text>
          </View>
        </View>
      ))}
    </View>
  </View>
);

// Documento
const VacunasAplicadasPdf = ({ patient, registrosVacunas, doctora }) => {
  const persona = patient || {};
  const registros = Array.isArray(registrosVacunas) ? registrosVacunas : [];

  const vacunasAgrupadas = agruparPorIdVacuna(registros);
  const vacunasArray = Object.entries(vacunasAgrupadas);

  const maxPorPagina = 4;
  const paginas = [];

  for (let i = 0; i < vacunasArray.length; i += maxPorPagina) {
    paginas.push(vacunasArray.slice(i, i + maxPorPagina));
  }

  return (
    <Document>
      {paginas.map((bloque, i) => (
        <Page key={i} size="A4" style={styles.page}>
          <Image style={styles.watermark} src="/images/longLogo.png" />
          <Image style={styles.logo} src="/images/headerright.png" />

          <Text style={styles.title}>Resumen de Vacunas Aplicadas</Text>

          {i === 0 && (
            <View style={styles.header}>
              <View>
                <Text style={styles.text}>
                  <Text style={styles.textBold}>Nombre:</Text>{" "}
                  {persona.nombreCompleto}
                </Text>
                <Text style={styles.text}>
                  <Text style={styles.textBold}>Nacimiento:</Text>{" "}
                  {formatFecha(persona.fecha_nacimiento)}
                </Text>
                <Text style={styles.text}>
                  <Text style={styles.textBold}>Sexo:</Text> {persona.sexo}
                </Text>
              </View>
              <View>
                <Text style={styles.text}>
                  <Text style={styles.textBold}>Peso:</Text> {persona.peso} kg
                </Text>
                <Text style={styles.text}>
                  <Text style={styles.textBold}>Altura:</Text> {persona.altura}{" "}
                  cm
                </Text>
              </View>
            </View>
          )}

          <View style={styles.section} wrap={false}>
            {bloque.map(([id, lista]) => (
              <TablaVacuna key={id} id={id} lista={lista} />
            ))}

            {i === paginas.length - 1 && (
              <View style={styles.firmas} wrap={false}>
                <DoctorSignature doctor={doctora} />
              </View>
            )}
          </View>
        </Page>
      ))}
    </Document>
  );
};

export default VacunasAplicadasPdf;
